import { Component, ViewEncapsulation, OnInit, ViewContainerRef, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, NavigationEnd, RouterModule } from "@angular/router";
import { ToastrService } from "ngx-toastr";

import { BusinessFunctionService } from "../../services/businessfunction.service";
import { IBusinessFunction } from "../../interfaces/ibusinessfunction";
import { ConfigurationService } from "../../services/configuration.service";
import { IAppStore } from "../../iappstore";
import { Store } from "@ngrx/store";
import { LoggedOutAction, ResetTokenExpiredAction, ResetUnauthorizedAction } from "../../actions/azureauthentication.action";
import { ResetNotFoundAction, ResetServerErrorAction, ResetBadRequestAction } from "../../actions/errorstatus.action";
import { filter } from "rxjs/operators";
import { LoadingIndicatorComponent } from "../loadingindicator/loadingindicator.component";
import { NavMenuComponent } from "../navmenu/navmenu.component";

@Component({
    selector: "app",
    standalone: true,
    imports: [CommonModule, RouterModule, LoadingIndicatorComponent, NavMenuComponent],
    encapsulation: ViewEncapsulation.None,
    templateUrl: "./app.component.html",
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class AppComponent implements OnInit {
    loggedIn: boolean = false;
    businessFunctionName: string;
    onlyExecuteOnce: boolean = true;
    isUnauthorized: boolean = false;
    constructor(
        private businessFunctionService: BusinessFunctionService,
        private router: Router,
        private toastrManager: ToastrService,
        private configurationService: ConfigurationService,
        private readonly store: Store<IAppStore>) {
    }

    /**
     * Initialiser
     */
    ngOnInit(): void {
        let businessFunction: IBusinessFunction;
        // reset logged out on app load
        this.store.dispatch(new LoggedOutAction(false));
        this.store.dispatch(new ResetTokenExpiredAction());
        this.store.dispatch(new ResetUnauthorizedAction());

        this.store.select(e => e.user.selectedBusinessFunction)
            .subscribe(bf => {
                this.loggedIn = bf != null && bf.id != null;
                this.setBusinessFunction(bf);
                businessFunction = bf;
            });

        this.store.select(e => e.azureAuthentication.loggedOut)
            .subscribe(e => {
                if (e) {
                    window.location.href =
                        `https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=
                    ${window.location.origin}/signedout`;
                }
            });

        // token expired
        this.store.select(e => e.azureAuthentication.tokenExpired)
            .subscribe(e => {
                if (e) {
                    this.configurationService.getLoginUrl().subscribe((url: string) => {
                        window.location.href = url;
                    });
                }
            });

        this.store.select(e => e.azureAuthentication.unauthorizedAccessAttempt)
            .subscribe(e => {
                if (e) {
                    this.isUnauthorized = true;
                    this.router.navigate(["/unauthorized"]);
                }
            });

        // Reset unauthorized state when navigating away from unauthorized page
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            if (event.url !== '/unauthorized') {
                this.isUnauthorized = false;
                this.store.dispatch(new ResetUnauthorizedAction());
            }
        });

        this.store.select(e => e.errorStatus.notfound)
            .subscribe(e => {
                this.store.dispatch(new ResetNotFoundAction());
                if (e) {
                    this.router.navigate(["/notfound"]);
                }
            });

        this.store.select(e => e.errorStatus.servererror)
            .subscribe(e => {
                this.store.dispatch(new ResetServerErrorAction());
                if (e) {
                    this.router.navigate(["/servererror"]);
                }
            });
        this.store.select(e => e.errorStatus.badrequest)
            .subscribe(e => {
                this.store.dispatch(new ResetBadRequestAction());
                // not implemented
            });

        this.loggedIn = businessFunction != null;
        this.setBusinessFunction(businessFunction);
    }

    ngAfterViewInit() {
        if (this.onlyExecuteOnce) {
            this.onlyExecuteOnce = false;
            this.isCookieLoaded();
        }
    }

    isCookieLoaded() {
        let script = document.createElement("script");
        script.setAttribute("src", "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js");
        script.setAttribute("data-document-language", "true");
        script.setAttribute("type", "text/javascript");
        script.setAttribute("charset", "UTF-8");
        const appUrl = document.location.href;
        switch (appUrl.length > 0) {
            case (appUrl.includes('localhost')): {
                script.setAttribute("data-domain-script", "018f7c38-d0e3-7ce7-9d1e-c3a7b89eb170-test");
                document.getElementsByTagName("head")[0].appendChild(script);
                break;
            }
            case (appUrl.includes('dev.taxcmsadmin.deloitte.com')): {
                script.setAttribute("data-domain-script", "018f7c38-d0e3-7ce7-9d1e-c3a7b89eb170-test");
                document.getElementsByTagName("head")[0].appendChild(script);
                break;
            }
            case (appUrl.includes('qa.taxcmsadmin.deloitte.com')): {
                script.setAttribute("data-domain-script", "018f7c38-d0e3-7ce7-9d1e-c3a7b89eb170-test");
                document.getElementsByTagName("head")[0].appendChild(script);
                break;
            }
            case (appUrl.includes('stage.taxcmsadmin.deloitte.com')): {
                script.setAttribute("data-domain-script", "018f7c38-d0e3-7ce7-9d1e-c3a7b89eb170-test");
                document.getElementsByTagName("head")[0].appendChild(script);
                break;
            }
            case (appUrl.includes('taxcmsadmin.deloitte.com')): {
                script.setAttribute("data-domain-script", "018f7c38-d0e3-7ce7-9d1e-c3a7b89eb170");
                document.getElementsByTagName("head")[0].appendChild(script);
                break;
            }

        }
    }

    /**
     * Opens the OneTrust cookie preferences modal
     */
    showCookieSettings(): void {
        const win = window as any;
        if (win.Optanon && typeof win.Optanon.ToggleInfoDisplay === 'function') {
            win.Optanon.ToggleInfoDisplay();
        } else if (win.OneTrust && typeof win.OneTrust.ToggleInfoDisplay === 'function') {
            win.OneTrust.ToggleInfoDisplay();
        } else {
            // SDK not loaded yet, try clicking the hidden element or show message
            const otButton = document.querySelector('.ot-sdk-show-settings') as HTMLElement;
            if (otButton) {
                otButton.click();
            }
        }
    }

    /**
     * Is called when the user signs out
     */
    signOutClicked(): void {
        this.store.dispatch(new LoggedOutAction(true));
        this.toastrManager.success("Logged out successfully");
    }

    /**
     * Sets the business function for the user, displays the function name in the menu
     * @param businessFunction The selected business function
     */
    private setBusinessFunction(businessFunction: IBusinessFunction): void {
        if (businessFunction && businessFunction.id) {
            this.businessFunctionName = businessFunction.displayName;
        } else {
            this.businessFunctionName = "Administrator Portal";
        }
    }
}

